import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { PageHeader } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { 
  Check, X, BadgeCheck, ShieldCheck, ZoomIn, ZoomOut, 
  RotateCw, RotateCcw, Eye, Sparkles, RefreshCw, UserCheck, 
  Download, FileText, AlertCircle, ShieldAlert 
} from "lucide-react";
import { toast } from "sonner";

function IdThumbnail({ verificationId, onClick }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let url;
    (async () => {
      try { 
        const res = await api.get(`/admin/verifications/${verificationId}/image`, { responseType: "blob" }); 
        url = URL.createObjectURL(res.data); 
        setSrc(url); 
      }
      catch { setFailed(true); }
    })();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [verificationId]);

  if (failed) return (
    <div className="w-36 h-24 rounded-xl bg-slate-100 flex flex-col items-center justify-center text-[11px] text-slate-400 border border-slate-200">
      <AlertCircle className="w-4 h-4 mb-1 text-slate-300" />
      <span>Unavailable</span>
    </div>
  );

  if (!src) return <div className="w-36 h-24 rounded-xl bg-slate-100 animate-pulse border border-slate-200" />;

  return (
    <div 
      onClick={() => onClick && onClick(src)}
      className="relative group cursor-pointer overflow-hidden rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex-shrink-0"
      title="Click to view & zoom document"
    >
      <img 
        src={src} 
        alt="Govt ID Document" 
        className="w-36 h-24 object-cover group-hover:scale-105 transition-transform duration-200" 
      />
      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5 text-white text-xs font-semibold">
        <Eye className="w-4 h-4" />
        <span>View</span>
      </div>
    </div>
  );
}

function DocViewerModal({ item, imageSrc, onClose, onReview, onAutoFetch, isExtracting }) {
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [docSrc, setDocSrc] = useState(imageSrc);
  const [loadingImg, setLoadingImg] = useState(!imageSrc);
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    if (imageSrc) {
      setDocSrc(imageSrc);
      setLoadingImg(false);
      return;
    }
    if (!item?.verification_id) return;

    let url;
    let isCancelled = false;
    setLoadingImg(true);
    setImgFailed(false);

    (async () => {
      try {
        const res = await api.get(`/admin/verifications/${item.verification_id}/image`, { responseType: "blob" });
        if (!isCancelled) {
          url = URL.createObjectURL(res.data);
          setDocSrc(url);
          setLoadingImg(false);
        }
      } catch {
        if (!isCancelled) {
          setImgFailed(true);
          setLoadingImg(false);
        }
      }
    })();

    return () => {
      isCancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [item?.verification_id, imageSrc]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  if (!item) return null;

  const verdict = item.verdict || {};
  const user = item.user || {};
  const isMatch = item.name_match;

  const handleDownload = () => {
    if (!docSrc) return;
    const a = document.createElement("a");
    a.href = docSrc;
    a.download = `ID_Verification_${user.name || "User"}_${item.verification_id}.jpg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 animate-in fade-in duration-150">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[92vh] flex flex-col overflow-hidden border border-slate-200">
        
        {/* Modal Top Bar */}
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between bg-slate-50/80">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#8F000D]/10 text-[#8F000D] flex items-center justify-center font-bold">
              <FileText className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-bold text-slate-800 text-sm sm:text-base flex items-center gap-1.5">
                <span>{user.name || "User ID Document"}</span>
                {user.is_verified && <BadgeCheck className="w-4 h-4 text-emerald-600" />}
                <span className="text-xs font-normal text-slate-500">({user.email || item.user_id})</span>
              </h3>
              <p className="text-xs text-slate-500">
                Uploaded: {new Date(item.created_at).toLocaleString()}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleDownload}
              className="text-xs h-8 gap-1.5 hidden sm:inline-flex"
            >
              <Download className="w-3.5 h-3.5" /> Download
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onClose}
              className="w-8 h-8 p-0 rounded-full text-slate-500 hover:text-slate-800 hover:bg-slate-200/50"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Modal Body: Left side Document Viewer, Right side KYC info */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 overflow-hidden">
          
          {/* Document Viewer Container (7 cols on lg) */}
          <div className="lg:col-span-7 bg-slate-900 relative flex flex-col items-center justify-center p-4 min-h-[320px] lg:min-h-[480px] overflow-hidden select-none">
            {/* Viewer Controls floating overlay */}
            <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5 bg-black/60 backdrop-blur-md px-2.5 py-1.5 rounded-xl border border-white/10 text-white text-xs">
              <button 
                onClick={() => setScale(s => Math.min(s + 0.25, 3))} 
                className="p-1 hover:bg-white/20 rounded transition" 
                title="Zoom In"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
              <span className="text-[11px] font-mono w-10 text-center">{Math.round(scale * 100)}%</span>
              <button 
                onClick={() => setScale(s => Math.max(s - 0.25, 0.5))} 
                className="p-1 hover:bg-white/20 rounded transition" 
                title="Zoom Out"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <div className="w-[1px] h-3 bg-white/20 mx-1" />
              <button 
                onClick={() => setRotation(r => (r + 90) % 360)} 
                className="p-1 hover:bg-white/20 rounded transition" 
                title="Rotate 90° Clockwise"
              >
                <RotateCw className="w-4 h-4" />
              </button>
              <button 
                onClick={() => { setScale(1); setRotation(0); }} 
                className="p-1 hover:bg-white/20 rounded transition text-[10px]" 
                title="Reset View"
              >
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Document Image with pan / zoom transform */}
            <div className="w-full h-full flex items-center justify-center overflow-auto p-4">
              {loadingImg ? (
                <div className="text-white/70 text-xs flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin text-amber-400" /> Loading document image...
                </div>
              ) : imgFailed ? (
                <div className="text-red-400 text-xs flex flex-col items-center gap-2">
                  <AlertCircle className="w-8 h-8 text-red-400" />
                  <span>Failed to load document image</span>
                </div>
              ) : docSrc ? (
                <img 
                  src={docSrc} 
                  alt="Full ID Document" 
                  style={{
                    transform: `scale(${scale}) rotate(${rotation}deg)`,
                    transition: "transform 0.15s ease-out"
                  }}
                  className="max-h-[70vh] max-w-full object-contain rounded-lg shadow-xl select-none"
                />
              ) : (
                <div className="text-white/50 text-xs">No image available</div>
              )}
            </div>
          </div>

          {/* KYC Details & Actions Panel (5 cols on lg) */}
          <div className="lg:col-span-5 bg-white p-5 flex flex-col justify-between overflow-y-auto max-h-[85vh] border-t lg:border-t-0 lg:border-l border-slate-100">
            <div className="space-y-4">
              
              {/* Auto-Fetch Header & Action */}
              <div className="bg-amber-50/70 border border-amber-200/80 rounded-xl p-3.5 flex items-center justify-between">
                <div>
                  <h4 className="text-xs font-bold text-amber-900 flex items-center gap-1.5">
                    <Sparkles className="w-4 h-4 text-amber-600" />
                    <span>Auto-Fetch Details (OCR & AI)</span>
                  </h4>
                  <p className="text-[11px] text-amber-800/80 mt-0.5">
                    Automatically scans and extracts name, doc type & DOB from ID
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => onAutoFetch(item.verification_id)}
                  disabled={isExtracting}
                  className="bg-amber-600 hover:bg-amber-700 text-white font-semibold text-xs h-8 gap-1.5 shadow-sm"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isExtracting ? "animate-spin" : ""}`} />
                  {isExtracting ? "Scanning..." : "Auto-Fetch"}
                </Button>
              </div>

              {/* Side-by-side comparison */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Profile vs. Document Data
                </h4>

                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-3 text-xs">
                  
                  {/* Name Comparison */}
                  <div>
                    <div className="text-slate-400 text-[10px] uppercase font-semibold">User Profile Name</div>
                    <div className="font-bold text-slate-800 text-sm">{user.name || "—"}</div>
                  </div>

                  <div className="pt-2 border-t border-slate-200">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400 text-[10px] uppercase font-semibold">Extracted Name on ID</span>
                      {verdict.name_on_doc && (
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          isMatch ? "bg-emerald-100 text-emerald-800 border border-emerald-300" : "bg-red-100 text-red-800 border border-red-300"
                        }`}>
                          {isMatch ? `✓ Name Match (${verdict.match_score || 100}%)` : "✗ No Direct Match"}
                        </span>
                      )}
                    </div>
                    <div className="font-bold text-slate-900 text-sm mt-0.5">
                      {verdict.name_on_doc || <span className="text-slate-400 font-normal italic">Not extracted yet (Click Auto-Fetch)</span>}
                    </div>
                  </div>

                  {/* Document Type */}
                  <div className="pt-2 border-t border-slate-200 grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-slate-400 text-[10px] uppercase font-semibold">Doc Type</div>
                      <div className="font-semibold text-slate-800 mt-0.5">
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold">
                          {verdict.doc_type || "Government ID"}
                        </span>
                      </div>
                    </div>
                    {verdict.id_number && (
                      <div>
                        <div className="text-slate-400 text-[10px] uppercase font-semibold">ID Number</div>
                        <div className="font-mono text-slate-800 mt-0.5 font-bold">{verdict.id_number}</div>
                      </div>
                    )}
                  </div>

                  {/* Father's Name / DOB if extracted */}
                  {(verdict.father_name || verdict.dob || user.dob) && (
                    <div className="pt-2 border-t border-slate-200 grid grid-cols-2 gap-2">
                      {verdict.father_name && (
                        <div>
                          <div className="text-slate-400 text-[10px] uppercase font-semibold">Father's Name (on ID)</div>
                          <div className="font-medium text-slate-800 mt-0.5">{verdict.father_name}</div>
                        </div>
                      )}
                      <div>
                        <div className="text-slate-400 text-[10px] uppercase font-semibold">DOB (ID / Profile)</div>
                        <div className="font-medium text-slate-800 mt-0.5">
                          {verdict.dob || "—"} / {user.dob || "—"}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Location & Status */}
                  <div className="pt-2 border-t border-slate-200 grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-slate-400 text-[10px] uppercase font-semibold">District</div>
                      <div className="font-medium text-slate-800 mt-0.5">{user.district || "—"}</div>
                    </div>
                    <div>
                      <div className="text-slate-400 text-[10px] uppercase font-semibold">Current Status</div>
                      <div className="mt-0.5">
                        <span className={`inline-flex px-2 py-0.5 rounded text-[11px] font-bold ${
                          item.status === "approved" ? "bg-emerald-100 text-emerald-800" :
                          item.status === "rejected" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"
                        }`}>
                          {item.status.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* AI / OCR Scan Note */}
                  {verdict.reason && (
                    <div className="pt-2 border-t border-slate-200 text-[11px] text-slate-500">
                      <span className="font-semibold text-slate-600">Scan Summary:</span> {verdict.reason}
                    </div>
                  )}

                </div>
              </div>

            </div>

            {/* Bottom Actions inside modal */}
            <div className="pt-4 border-t border-slate-200 mt-4 flex items-center justify-between gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onClose}
                className="text-xs"
              >
                Close
              </Button>

              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { onReview(item.verification_id, "reject"); onClose(); }}
                  className="text-red-600 border-red-200 hover:bg-red-50 text-xs font-bold"
                >
                  <X className="w-3.5 h-3.5 mr-1" /> Reject
                </Button>
                <Button
                  size="sm"
                  onClick={() => { onReview(item.verification_id, "approve"); onClose(); }}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold shadow-sm"
                >
                  <Check className="w-3.5 h-3.5 mr-1" /> Approve ID
                </Button>
              </div>
            </div>

          </div>

        </div>

      </div>
    </div>
  );
}

export default function Verifications() {
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedImageSrc, setSelectedImageSrc] = useState(null);
  const [extractingId, setExtractingId] = useState(null);

  const load = async () => {
    try {
      const [p, a] = await Promise.all([
        api.get("/admin/verifications", { params: { status: "pending" } }),
        api.get("/admin/verifications", { params: { status: "approved" } }),
      ]);
      setPending(p.data); 
      setApproved(a.data);
    } catch (e) { 
      toast.error(formatError(e)); 
    }
  };

  useEffect(() => { load(); }, []);

  const review = async (id, action) => {
    try { 
      await api.post(`/admin/verifications/${id}/review?action=${action}`); 
      toast.success(`Verification ${action}d successfully`); 
      load(); 
    }
    catch (e) { 
      toast.error(formatError(e)); 
    }
  };

  const autoFetch = async (verificationId) => {
    setExtractingId(verificationId);
    try {
      const res = await api.post(`/admin/verifications/${verificationId}/extract`);
      toast.success(res.data.message || "Auto-fetch complete!");
      // Reload list and update selected modal item if open
      await load();
      if (selectedItem && selectedItem.verification_id === verificationId) {
        setSelectedItem(prev => ({
          ...prev,
          verdict: res.data.verdict,
          name_match: res.data.name_match
        }));
      }
    } catch (e) {
      toast.error(formatError(e) || "Auto-fetch failed");
    } finally {
      setExtractingId(null);
    }
  };

  const handleOpenDoc = (item, src) => {
    setSelectedItem(item);
    setSelectedImageSrc(src);
  };

  return (
    <div>
      <PageHeader 
        title="ID Verifications" 
        subtitle="Inspect government ID submissions, auto-fetch names, and approve verified users" 
      />

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" data-testid="adminver-tab-pending">
            Pending ({pending.length})
          </TabsTrigger>
          <TabsTrigger value="approved" data-testid="adminver-tab-approved">
            Approved ({approved.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-6">
          <List 
            items={pending} 
            onReview={review} 
            onOpenDoc={handleOpenDoc}
            onAutoFetch={autoFetch}
            extractingId={extractingId}
            canAct 
          />
        </TabsContent>

        <TabsContent value="approved" className="mt-6">
          <List 
            items={approved} 
            onReview={review} 
            onOpenDoc={handleOpenDoc}
            onAutoFetch={autoFetch}
            extractingId={extractingId}
            isApproved 
          />
        </TabsContent>
      </Tabs>

      {/* Full Document Viewer Modal */}
      {selectedItem && (
        <DocViewerModal
          item={selectedItem}
          imageSrc={selectedImageSrc}
          onClose={() => { setSelectedItem(null); setSelectedImageSrc(null); }}
          onReview={review}
          onAutoFetch={autoFetch}
          isExtracting={extractingId === selectedItem.verification_id}
        />
      )}
    </div>
  );
}

function List({ items, onReview, onOpenDoc, onAutoFetch, extractingId, canAct, isApproved }) {
  if (!items.length) {
    return (
      <div className="flex flex-col items-center justify-center p-12 border-2 border-dashed border-slate-200 rounded-2xl bg-slate-50/50">
        <ShieldCheck className="w-12 h-12 text-slate-300 mb-3" />
        <p className="text-slate-500 font-medium">No verifications in this list.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {items.map((v) => {
        const verdict = v.verdict || {};
        const isMatch = v.name_match;
        const isExtracting = extractingId === v.verification_id;

        return (
          <div 
            key={v.verification_id} 
            className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow transition-shadow p-4 sm:p-5" 
            data-testid={`verification-${v.verification_id}`}
          >
            <div className="flex flex-col sm:flex-row items-start gap-4">
              
              {/* Document Thumbnail with Click to Open */}
              <IdThumbnail 
                verificationId={v.verification_id} 
                onClick={(src) => onOpenDoc(v, src)} 
              />

              {/* Information Column */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-bold text-slate-900 text-base">{v.user?.name || "Unknown User"}</span>
                  {v.user?.is_verified && (
                    <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                      <BadgeCheck className="w-3.5 h-3.5" /> Verified
                    </span>
                  )}
                  <span className="text-xs text-slate-400">
                    · {v.user?.district || "Jharkhand"} · {new Date(v.created_at).toLocaleString()}
                  </span>
                </div>

                <div className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-3">
                  <span>{v.user?.email}</span>
                  {v.user?.phone && <span>· 📱 +91 {v.user.phone}</span>}
                  {v.user?.dob && <span>· 🎂 DOB: {v.user.dob}</span>}
                </div>

                {/* Structured Extracted Box */}
                <div className="text-sm mt-3 bg-slate-50/90 rounded-xl p-3.5 border border-slate-200/80 text-slate-700 space-y-1.5">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    <div>
                      Doc type: <b className="text-slate-900">{verdict.doc_type || "—"}</b>
                    </div>
                    <div>
                      Name on ID: <b className="text-slate-900">{verdict.name_on_doc || "—"}</b>
                    </div>
                    {verdict.father_name && (
                      <div>
                        Father's Name: <b className="text-slate-900">{verdict.father_name}</b>
                      </div>
                    )}
                    {verdict.dob && (
                      <div>
                        DOB on ID: <b className="text-slate-900">{verdict.dob}</b>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-2 pt-1 border-t border-slate-200/60">
                    <div className="flex items-center gap-2">
                      <span>Name match:</span>
                      <b className={`px-2 py-0.5 rounded text-xs font-bold ${
                        isMatch ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"
                      }`}>
                        {isMatch ? `Yes (${verdict.match_score || 100}%)` : "No"}
                      </b>
                      {verdict.reason && (
                        <span className="text-xs text-slate-500">· {verdict.reason}</span>
                      )}
                    </div>

                    {/* Auto Fetch button on card */}
                    <button
                      onClick={() => onAutoFetch(v.verification_id)}
                      disabled={isExtracting}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[#8F000D] hover:underline"
                    >
                      <Sparkles className={`w-3.5 h-3.5 ${isExtracting ? "animate-spin" : ""}`} />
                      <span>{isExtracting ? "Scanning..." : "Auto-Fetch Details"}</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Action Buttons Column */}
              <div className="flex flex-col sm:flex-row lg:flex-col gap-2 flex-shrink-0 w-full sm:w-auto mt-2 sm:mt-0">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => onOpenDoc(v, null)}
                  className="text-xs h-9 justify-center gap-1.5"
                >
                  <Eye className="w-3.5 h-3.5" /> View Full ID
                </Button>

                {canAct && (
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-9 flex-1 sm:flex-initial" 
                      onClick={() => onReview(v.verification_id, "approve")} 
                      data-testid={`approve-${v.verification_id}`}
                    >
                      <Check className="w-3.5 h-3.5 mr-1" /> Approve
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="text-red-600 border-red-200 hover:bg-red-50 text-xs h-9 flex-1 sm:flex-initial" 
                      onClick={() => onReview(v.verification_id, "reject")} 
                      data-testid={`reject-${v.verification_id}`}
                    >
                      <X className="w-3.5 h-3.5 mr-1" /> Reject
                    </Button>
                  </div>
                )}

                {isApproved && (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="text-red-600 border-red-200 hover:bg-red-50 text-xs h-9" 
                    onClick={() => onReview(v.verification_id, "reject")} 
                    data-testid={`reject-${v.verification_id}`}
                  >
                    <X className="w-3.5 h-3.5 mr-1" /> Reject
                  </Button>
                )}
              </div>

            </div>
          </div>
        );
      })}
    </div>
  );
}
